/**
 * useChamadosList — listagem de chamados com roteamento por scope.
 *
 * Spec: docs/CHAMADOS_VISIBILIDADE_SPEC.md §9.2.2
 *
 * Caracteristicas:
 *  - queryKey scope-first (`['chamados', scope, filters]`) — gap C7:
 *    isola o cache do TanStack Query entre os modos de visualizacao.
 *  - Roteamento por scope:
 *      'global' -> GET /v1/chamados
 *      'org'    -> GET /v1/organizations/{org_id}/chamados (lanca se sem org_id)
 *      'own'    -> GET /v1/me/chamados/created
 *  - enabled=false quando scope='org' sem organization_id (evita request vazio)
 *  - staleTime diferenciado: 30s para global, 5s para org/own.
 *
 * Notas:
 *  - api client tem baseURL '/api', portanto usamos paths '/v1/...'.
 *  - Filtros de array (status, priority, tenant_ids) sao serializados como CSV.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { chamadosApi, type SubTaskItem } from '@/api/chamados'
import type { ChamadoScope } from './useScope'

export interface ChamadosListFilters {
  status?: string[]
  priority?: string[]
  category_id?: number
  request_type?: string
  assignee_id?: number | 'me'
  requester_id?: number | 'me'
  q?: string
  resource_type?: string
  created_from?: string
  created_to?: string
  tenant_ids?: number[]
  organization_id?: number
  limit?: number
  offset?: number
}

export interface ChamadoItemPermissions {
  can_approve: boolean
  can_assign: boolean
  can_close: boolean
  can_comment: boolean
}

export interface ChamadoItem {
  id: number
  tenant_id: number
  tenant_name: string
  environment: string | null
  title: string
  status: string
  priority: string | null
  request_type: string | null
  protocol_number: string | null
  category_id: number | null
  category_name: string | null
  requested_by_user_id: number
  requested_by_name: string | null
  assignee_user_id: number | null
  assignee_name: string | null
  origin: string
  created_at: string
  updated_at: string
  // Task 13 — coluna "Time" na lista. Optional pra retrocompat (chamados
  // legacy com target_template_id=NULL e periodo de rollout do backend).
  target_template_id?: number | null
  target_template_name?: string | null
  permissions: ChamadoItemPermissions
  // Sub-tarefas (group_subtasks=true): id do pai (null = pai/avulso) e rollup.
  parent_task_id?: number | null
  sub_task_count?: number
  sub_task_done?: number
  // Organização do tenant do chamado (visão "Minha organização" cross-org).
  organization_name?: string | null
}

export interface ChamadosListResponse {
  items: ChamadoItem[]
  total: number
  limit: number
  offset: number
  applied_scope: ChamadoScope
  organization_id: number | null
  resolved_tenant_count: number | null
  downgrade_reason: string | null
}

interface UseChamadosListArgs {
  scope: ChamadoScope
  filters?: ChamadosListFilters
  enabled?: boolean
}

function buildPath(scope: ChamadoScope, filters: ChamadosListFilters): string {
  const params = new URLSearchParams()

  if (filters.status?.length) params.set('status', filters.status.join(','))
  if (filters.priority?.length) params.set('priority', filters.priority.join(','))
  if (filters.category_id !== undefined) params.set('category_id', String(filters.category_id))
  if (filters.request_type) params.set('request_type', filters.request_type)
  if (filters.assignee_id !== undefined) params.set('assignee_id', String(filters.assignee_id))
  if (filters.requester_id !== undefined) params.set('requester_id', String(filters.requester_id))
  if (filters.q) params.set('q', filters.q)
  if (filters.resource_type) params.set('resource_type', filters.resource_type)
  if (filters.created_from) params.set('created_from', filters.created_from)
  if (filters.created_to) params.set('created_to', filters.created_to)
  if (filters.tenant_ids?.length) params.set('tenant_ids', filters.tenant_ids.join(','))
  if (filters.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters.offset !== undefined) params.set('offset', String(filters.offset))
  // Sempre agrupar sub-tarefas sob o pai na listagem (spec 2026-07-21).
  params.set('group_subtasks', 'true')

  let basePath: string
  if (scope === 'global') {
    basePath = '/v1/chamados'
  } else if (scope === 'org') {
    if (!filters.organization_id) {
      throw new Error('organization_id required for scope=org')
    }
    basePath = `/v1/organizations/${filters.organization_id}/chamados`
  } else {
    basePath = '/v1/me/chamados/created'
    if (filters.organization_id !== undefined) {
      params.set('organization_id', String(filters.organization_id))
    }
  }

  const qs = params.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

export function useChamadosList({ scope, filters = {}, enabled = true }: UseChamadosListArgs) {
  // Gate: scope='org' sem organization_id evita request vazio (4xx garantido)
  const canFetch = scope !== 'org' || !!filters.organization_id

  return useQuery<ChamadosListResponse>({
    // queryKey COMECA por scope para nao vazar cache entre modos (gap C7)
    queryKey: ['chamados', scope, filters],
    queryFn: async () => {
      const path = buildPath(scope, filters)
      const res = await api.get<ChamadosListResponse>(path)
      return res.data
    },
    enabled: enabled && canFetch,
    // staleTime diferenciado por scope (spec §9.2.2)
    staleTime: scope === 'global' ? 30_000 : 5_000,
    retry: false,
  })
}

/**
 * useSubTasks — carrega as sub-tarefas de um chamado-pai sob demanda (ao expandir
 * a linha na listagem). Reusa o endpoint existente GET /workflow/{id}/sub-tasks.
 */
export function useSubTasks(tenantId: number, parentId: number, enabled: boolean) {
  return useQuery<SubTaskItem[]>({
    queryKey: ['chamado-subtasks', tenantId, parentId],
    queryFn: async () => {
      const res = await chamadosApi.subTasks(tenantId, parentId)
      return res.data
    },
    enabled: enabled && !!tenantId && !!parentId,
    staleTime: 30_000,
    retry: false,
  })
}
